import React from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import queryString from 'query-string';
import debounce from 'lodash/debounce';
import history from '../../history';
import withIntersectionObserver from '../../decorators/withIntersectionObserver';
import { getShopParams, isProductPorInPortal } from '../../helpers';
import {
  getProducts,
  updateFilters,
  updatePriceIntervals,
  sortProductsBy,
} from '../../actions/shop';

import { resetErrorState } from '../../actions/basket';
import { showError } from '../../components/Notifications/notificationTypes';
import ProductListPage from '../../components/ProductListPage';
import { SALES_CHANNELS, SALES_CHANNELS_GROUP } from '../../constants/index';
import ApplicationContext from '../../components/ApplicationContext';

class Shop extends React.Component {
  constructor(props) {
    super(props);
    this.onPriceIntervalChange = debounce(this.onPriceIntervalChange, 500);
  }

  componentDidMount() {
    const { errorToastify, errorToastifyFull } = this.props;
    if (errorToastify) {
      const { errors } = errorToastifyFull;
      showError({ errors });
      this.props.dispatch(resetErrorState());
    }
  }

  componentDidUpdate() {
    const {
      isBottom,
      shop: { nextPage, infiniteLoad, loading },
    } = this.props;

    if (isBottom && !loading && infiniteLoad) {
      this.loadProducts(nextPage);
    }
  }

  onFilterChange = (id, checked, group) => {
    this.props.dispatch(updateFilters(id, checked, group));
  };

  onPriceIntervalChange = (min, max) => {
    const {
      shop: { priceInterval },
    } = this.props;
    if (
      !Number.isNaN(min) &&
      !Number.isNaN(max) &&
      (min !== priceInterval.min || max !== priceInterval.max)
    ) {
      this.props.dispatch(updatePriceIntervals(min, max));
    }
  };

  onSortBy = value => {
    this.props.dispatch(sortProductsBy(value));
  };

  loadProducts = page => {
    const { search } = history.location || {};
    const parsedSearch = queryString.parse(search);
    this.props.dispatch(
      getProducts({
        page,
        id: this.props.salesChannelId,
        params: getShopParams(parsedSearch),
      }),
    );
  };

  getBanner = (page, salesChannelId, presentations) => {
    let banner;
    // if country has banner, choose that instead, over page banner
    if (presentations?.countryBannerImage) {
      banner = [
        {
          desktop: presentations?.countryBannerImage?.source || undefined,
          mobile: presentations?.countryBannerImageMobile?.source || undefined,
          alt: '',
          link: presentations?.bannerImageLink || '',
        },
      ];
    } else if (Object.values(SALES_CHANNELS).includes(salesChannelId)) {
      banner = page?.banners?.map(b => ({
        desktop: b.image || undefined,
        mobile: b.mobileImage || undefined,
        alt: b.title || '',
        id: b.id || undefined,
        link: b.link || '',
      }));
    } else {
      banner = [
        {
          desktop: page?.portalBannerDesktop || undefined,
          mobile: page?.portalBannerMobile || undefined,
          alt: page?.pageName || '',
          link: page?.pageBannerLink || '',
        },
      ];
    }
    return banner;
  };

  static contextType = ApplicationContext;

  render() {
    const {
      shop,
      dispatch,
      user,
      mode,
      page,
      salesChannelId,
      salesChannelGroup,
      presentations,
      ...props
    } = this.props;

    const { data } = shop;

    // temp solution to hide POR giftcards in portal, should be possible to decide if certain giftcards should be visible in GMP or Mainsite or both.
    // Also hide porducts ot of stock in portals
    const filteredProducts = data.filter(item =>
      salesChannelGroup === SALES_CHANNELS_GROUP.PORTAL
        ? item?.deliveryMethods.some(delivery => delivery.hasPricesInStock) &&
          !isProductPorInPortal(item.productTitle, salesChannelGroup)
        : !isProductPorInPortal(item.productTitle, salesChannelGroup),
    );

    return (
      <ProductListPage
        {...shop}
        {...props}
        data={filteredProducts}
        mode={mode}
        onFilterChange={this.onFilterChange}
        onPriceIntervalChange={this.onPriceIntervalChange}
        onSortBy={this.onSortBy}
        loadProducts={this.loadProducts}
        banner={this.getBanner(page, salesChannelId, presentations)}
        salesChannelGroup={salesChannelGroup}
        presentations={presentations}
        salesChannelId={salesChannelId}
        catalogMode={props.catalogMode}
      />
    );
  }
}

Shop.propTypes = {
  shop: PropTypes.shape().isRequired,
  salesChannelId: PropTypes.string.isRequired,
  salesChannelGroup: PropTypes.string.isRequired,
  isIntersecting: PropTypes.bool.isRequired,
  isBottom: PropTypes.bool.isRequired,
  dispatch: PropTypes.func.isRequired,
  errorToastify: PropTypes.bool,
  errorToastifyFull: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.string,
      field: PropTypes.oneOfType(PropTypes.func),
      message: PropTypes.string,
      path: PropTypes.oneOfType(PropTypes.func),
    }),
  ),
  mode: PropTypes.string.isRequired,
  user: PropTypes.shape(),
  page: PropTypes.shape({
    portalBannerDesktop: PropTypes.string,
    portalBannerMobile: PropTypes.string,
    pageTitle: PropTypes.string,
    banners: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        image: PropTypes.string,
        title: PropTypes.string,
        mobileImage: PropTypes.string,
        link: PropTypes.string,
      }),
    ),
  }),
  presentations: PropTypes.shape(),
  catalogMode: PropTypes.bool,
};

Shop.defaultProps = {
  errorToastify: undefined,
  user: null,
  errorToastifyFull: [
    {
      code: '',
      field: undefined,
      message: '',
      path: undefined,
    },
  ],
  page: {
    portalBannerDesktop: undefined,
    portalBannerMobile: undefined,
    pageTitle: undefined,
    banners: [
      {
        id: undefined,
        image: undefined,
        title: '',
        mobileImage: undefined,
        link: '',
      },
    ],
  },
  presentations: null,
  catalogMode: false,
};

const mapState = state => {
  const pageName = Object.values(SALES_CHANNELS).includes(
    state.config.salesChannelId,
  )
    ? 'shop'
    : `portal_${state.config.salesChannelId}`;

  const country = state.intl.countryCode;

  return {
    shop: state.shop,
    currency: state.intl.currency,
    redeem: state.basket.totalRedeemAmount,
    mode: state.user.mode,
    user: state.user.data,
    errorToastify: state.basket.errorToastify,
    errorToastifyFull: state.basket.errorToastifyFull,
    salesChannelId: state.config.salesChannelId,
    salesChannelGroup: state.config.salesChannel.group,
    page: state.page.byName[pageName],
    presentations: state.config.presentations[country],
  };
};

export default compose(withIntersectionObserver(1), connect(mapState))(Shop);
